import ExcelJS from 'exceljs';
import { JobType, MeterRateTaskType } from '../../redux/jobs/jobs.types';
import { formatDate } from '../../helper/DateHelper';
import { revolveMeterTypeByObis } from '../../helper/MeterHelper';
import { getLatestReadingEvent, getLatestReadingResult } from '../../helper/JobHelper';
import { sanitizeCellText } from './ColumnValueResolver';
import i18n from '../../i18n';

// Faithful reconstruction of the pre-296 legacy "Excel-Datei herunterladen"
// format (git show 66e61d78^:src/screens/common/ExportExcel/ExportExcel.tsx):
// one flat sheet, one header row, no styling, no protection. Deliberately
// kept separate from ExcelWorkbookBuilder.ts — same shared data helpers
// (JobHelper/MeterHelper), different, permanently frozen layout.
interface LegacyColumn {
    headerKey: string;
    getValue: (job: JobType, task: MeterRateTaskType) => string | number;
}

function translateMeterType(meterType: string): string {
    const translated = i18n.t(`common.metertypes.${meterType}`) as string;
    return translated || meterType;
}

function resolveMeterTypeText(job: JobType, task: MeterRateTaskType): string {
    if (job.meter.customMeterType) return job.meter.customMeterType;
    if (job.meter.meterType) return translateMeterType(job.meter.meterType);
    return translateMeterType(revolveMeterTypeByObis(task.obis));
}

const LEGACY_COLUMNS: LegacyColumn[] = [
    { headerKey: 'partnerIdentifier', getValue: (job) => job.partner?.identifier ?? '' },
    { headerKey: 'partnerCompany', getValue: (job) => job.partner?.company ?? '' },
    { headerKey: 'customerNumber', getValue: (job) => job.customer.customerNumber ?? '' },
    { headerKey: 'customerCompany', getValue: (job) => job.customer.company ?? '' },
    { headerKey: 'customerMailingAddressStreet', getValue: (job) => job.customer.mailingAddress?.street ?? '' },
    { headerKey: 'customerMailingAddressHouseNumber', getValue: (job) => job.customer.mailingAddress?.houseNumber ?? '' },
    { headerKey: 'customerMailingAddressZip', getValue: (job) => job.customer.mailingAddress?.zip ?? '' },
    { headerKey: 'customerMailingAddressCity', getValue: (job) => job.customer.mailingAddress?.city ?? '' },
    { headerKey: 'maLoId', getValue: (job) => job.meter.maLoId ?? '' },
    { headerKey: 'meterAddressStreet', getValue: (job) => job.meter.meterAddress?.street ?? '' },
    { headerKey: 'meterAddressHouseNumber', getValue: (job) => job.meter.meterAddress?.houseNumber ?? '' },
    { headerKey: 'meterAddressZip', getValue: (job) => job.meter.meterAddress?.zip ?? '' },
    { headerKey: 'meterAddressCity', getValue: (job) => job.meter.meterAddress?.city ?? '' },
    { headerKey: 'meterAddressObjectKey', getValue: (job) => job.meter.meterAddress?.objectKey ?? '' },
    { headerKey: 'meterAddressLocationDescription', getValue: (job) => job.meter.meterAddress?.locationDescription ?? '' },
    { headerKey: 'meterNumber', getValue: (job) => job.meter.meterNumber ?? '' },
    { headerKey: 'meterType', getValue: resolveMeterTypeText },
    { headerKey: 'rateName', getValue: (_job, task) => task.rateName ?? '' },
    { headerKey: 'obis', getValue: (_job, task) => task.obis ?? '' },
    { headerKey: 'targetReadingDate', getValue: (_job, task) => formatDate(task.targetReadingDate) ?? '' },
    { headerKey: 'latestExecution', getValue: (job) => formatDate(job.jobConstraints?.latestExecution) ?? '' },
    { headerKey: 'meterAddressHint', getValue: (job) => job.meter.meterAddress?.hint ?? '' },
    { headerKey: 'lastMeterReadingDate', getValue: (_job, task) => formatDate(getLatestReadingEvent(task)?.readingDate) ?? '' },
    { headerKey: 'lastMeterReadingValue', getValue: (_job, task) => getLatestReadingEvent(task)?.readingValue ?? '' },
    { headerKey: 'meterReadingDate', getValue: (_job, task) => formatDate(getLatestReadingResult(task)?.readingDate) ?? '' },
    { headerKey: 'meterReadingValue', getValue: (_job, task) => getLatestReadingResult(task)?.readingValue ?? '' },
];

export async function buildLegacyShareWorkbook(jobs: JobType[]): Promise<Blob> {
    const headerRow = LEGACY_COLUMNS.map(
        (col) => i18n.t(`management.accounts.share.exportXlsx.headers.${col.headerKey}`) as string
    );

    const rows: Array<Array<string | number>> = jobs.flatMap((job) =>
        job.meter.meterRateTasks.map((task) =>
            LEGACY_COLUMNS.map((col) => {
                const value = col.getValue(job, task);
                return typeof value === 'string' ? sanitizeCellText(value) : value;
            })
        )
    );

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Excel Download');
    worksheet.addRow(headerRow);
    rows.forEach((row) => worksheet.addRow(row));

    // Auto-fit column widths: max content length (+1), matching the original fitToColumn.
    LEGACY_COLUMNS.forEach((_col, index) => {
        const lengths = [headerRow[index].length, ...rows.map((row) => String(row[index] ?? '').length)];
        worksheet.getColumn(index + 1).width = Math.max(...lengths) + 1;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    return new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
}
